import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConsultationService } from '../../services/consultation.service';

const CONSULTATIONS_STORAGE_KEY = 'ambition_consultations_list_v1';

@Component({
  selector: 'app-consultation-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './consultation-modal.component.html',
  styleUrl: './consultation-modal.component.scss'
})
export class ConsultationModalComponent {
  private fb = inject(FormBuilder);
  private consultationService = inject(ConsultationService);

  isModalOpen = this.consultationService.isModalOpen;
  prefillData = this.consultationService.prefillData;

  bookingForm: FormGroup = this.fb.group({
    fullName: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, Validators.pattern(/^[0-9\s\+\-]{8,15}$/)]],
    projectType: ['Custom New Home', Validators.required],
    suburb: ['', Validators.required],
    budgetRange: ['$1.5M - $2.5M'],
    message: ['']
  });

  isSubmitting = signal<boolean>(false);
  isSubmitted = signal<boolean>(false);
  referenceCode = signal<string>('');

  close() {
    this.consultationService.closeModal();
    this.isSubmitted.set(false);
  }

  private saveToLocalStorage(payload: any) {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const saved = localStorage.getItem(CONSULTATIONS_STORAGE_KEY);
        const list = saved ? JSON.parse(saved) : [];
        const newItem = {
          id: Date.now(),
          full_name: payload.fullName,
          email: payload.email,
          phone: payload.phone,
          project_type: payload.projectType,
          suburb: payload.suburb,
          budget_range: payload.budgetRange,
          message: payload.message,
          reference_code: payload.refCode,
          created_at: new Date().toLocaleString()
        };
        list.unshift(newItem);
        localStorage.setItem(CONSULTATIONS_STORAGE_KEY, JSON.stringify(list));
      } catch (e) {}
    }
  }

  onSubmit() {
    if (this.bookingForm.valid) {
      this.isSubmitting.set(true);
      const ref = 'AB-' + Math.floor(100000 + Math.random() * 900000);
      this.referenceCode.set(ref);

      const payload = {
        ...this.bookingForm.value,
        refCode: ref
      };

      // Always save to LocalStorage immediately so inquiry is available locally
      this.saveToLocalStorage(payload);

      const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

      if (isLocalhost) {
        // On localhost Node dev server, complete smoothly without triggering a 404 PHP request
        setTimeout(() => {
          this.isSubmitting.set(false);
          this.isSubmitted.set(true);
        }, 300);
        return;
      }

      // On Live Hostinger server: Post to PHP REST API (saves to MySQL database and dispatches email)
      fetch('/api/consultations.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      .then(() => {
        this.isSubmitting.set(false);
        this.isSubmitted.set(true);
      })
      .catch(() => {
        this.isSubmitting.set(false);
        this.isSubmitted.set(true);
      });
    } else {
      this.bookingForm.markAllAsTouched();
    }
  }
}
